import { useState } from 'react';
import { useSelector } from 'react-redux';
import { UNDERWRITING_STATUS_CONFIG, SessionType } from '@shared';
import { selectSession } from '@client/model';
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@design-system';
import { ChevronDown } from 'lucide-react';

interface StructuredSlideOutContentProps {
  structuredResult: StructuredUnderwritingResult;
  pipelineMetadata?: UnderwritingPipelineMetadata | null;
}

const RATE_CLASS_LABELS: Record<string, string> = {
  preferred: 'Preferred',
  standard: 'Standard',
  tobacco: 'Tobacco',
  non_tobacco: 'Non-Tobacco',
  not_available: 'Not Available',
};

function getOutcomeLabel(outcome: string) {
  if (outcome === 'eligible') return UNDERWRITING_STATUS_CONFIG.eligible.label;
  if (outcome === 'ineligible') return UNDERWRITING_STATUS_CONFIG.ineligible.label;
  return UNDERWRITING_STATUS_CONFIG.unsure.label;
}

function getOutcomeConfig(outcome: string) {
  if (outcome === 'eligible') return UNDERWRITING_STATUS_CONFIG.eligible;
  if (outcome === 'ineligible') return UNDERWRITING_STATUS_CONFIG.ineligible;
  return UNDERWRITING_STATUS_CONFIG.unsure;
}

function StatusBadge({ outcome }: { outcome: string }) {
  const config = getOutcomeConfig(outcome);
  return (
    <span
      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${config.classes.bgColor} ${config.classes.textColor}`}
    >
      {getOutcomeLabel(outcome)}
    </span>
  );
}

function SectionHeader({
  title,
  count,
  isOpen,
}: {
  title: string;
  count?: number;
  isOpen: boolean;
}) {
  return (
    <div className="flex w-full items-center justify-between">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {count !== undefined && (
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
            {count}
          </span>
        )}
      </div>
      <ChevronDown
        className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
      />
    </div>
  );
}

function Disclaimer() {
  return (
    <div className="rounded-lg bg-gray-50 p-4">
      <div className="text-primary-dark text-xs">
        <p className="mb-2">
          <span className="font-semibold">Disclaimer:</span> This analysis is generated by a Wealth
          Assurance Group AI tool, <span className="font-semibold">not</span> by the insurance
          carrier. It is for informational purposes only and does not guarantee your final
          underwriting outcome.
        </p>
        <ul className="ml-4 list-disc space-y-1 pl-2">
          <li className="pl-1">
            <span className="font-semibold">AI can make mistakes.</span> You are responsible for
            verifying these insights.
          </li>
          <li className="pl-1">
            <span className="font-semibold">This is not an offer of coverage.</span> All final
            eligibility and pricing decisions are made exclusively by the carrier's underwriters.
          </li>
          <li className="pl-1">
            <span className="font-semibold">These insights are not advice.</span> They are
            informational observations based upon what is known by Wealth Assurance Group about the
            applicant and the carrier's eligibility guidelines, it is not professional insurance,
            financial, medical, or legal advice.
          </li>
        </ul>
      </div>
    </div>
  );
}

function WizardDiagnosticsDialog({
  structuredResult,
  pipelineMetadata,
}: {
  structuredResult: StructuredUnderwritingResult;
  pipelineMetadata: UnderwritingPipelineMetadata;
}) {
  const [open, setOpen] = useState(false);

  const { verification, assessorOutput, reviewStatus } = pipelineMetadata;
  const correctionCount = verification?.corrections?.length ?? 0;
  const missedRuleCount = verification?.missed_rules?.length ?? 0;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="w-full text-xs"
      >
        View Diagnostics
        {verification && !verification.confirmed && (
          <span className="ml-1.5 rounded bg-gray-200 px-1.5 py-0.5 text-xs font-medium text-gray-700">
            {correctionCount} correction{correctionCount !== 1 ? 's' : ''}
          </span>
        )}
      </Button>

      <Dialog
        open={open}
        onOpenChange={setOpen}
      >
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Pipeline Diagnostics</DialogTitle>
            <DialogDescription>
              <span className="flex items-center gap-2">
                <span>
                  Review status: {reviewStatus === 'reviewed' ? 'Reviewed' : 'Unreviewed'}
                </span>
                {verification && (
                  <span>
                    {verification.confirmed
                      ? '- Confirmed'
                      : `- ${correctionCount} correction${correctionCount !== 1 ? 's' : ''}, ${missedRuleCount} missed rule${missedRuleCount !== 1 ? 's' : ''}`}
                  </span>
                )}
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div>
              <h4 className="mb-1 text-xs font-semibold tracking-wider text-gray-500 uppercase">
                Rationale
              </h4>
              <p className="text-sm text-gray-900">{structuredResult.rationale}</p>
            </div>

            {verification && correctionCount > 0 && (
              <div>
                <h4 className="mb-2 text-xs font-semibold tracking-wider text-gray-500 uppercase">
                  Corrections
                </h4>
                <div className="space-y-3">
                  {verification.corrections.map((correction, i) => (
                    <div
                      key={i}
                      className="rounded border border-gray-200 bg-gray-50 p-3"
                    >
                      <div className="mb-1.5 text-xs font-semibold text-gray-900">
                        {correction.field}
                      </div>
                      <div className="space-y-1 text-xs">
                        <div className="text-gray-700">
                          <span className="font-medium">Before: </span>
                          <span className="line-through">{correction.original_value}</span>
                        </div>
                        <div className="text-gray-700">
                          <span className="font-medium">After: </span>
                          {correction.corrected_value}
                        </div>
                        <div className="mt-1.5 text-gray-600">{correction.reason}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {verification && missedRuleCount > 0 && (
              <div>
                <h4 className="mb-2 text-xs font-semibold tracking-wider text-gray-500 uppercase">
                  Missed Rules
                </h4>
                <div className="space-y-2">
                  {verification.missed_rules.map((rule, i) => (
                    <div
                      key={i}
                      className="rounded border border-gray-200 bg-gray-50 p-3"
                    >
                      <div className="text-xs font-medium text-gray-900">{rule.rule}</div>
                      <div className="mt-1 text-xs text-gray-600">{rule.impact}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {assessorOutput && <AssessorOutputSection assessorOutput={assessorOutput} />}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AssessorOutputSection({ assessorOutput }: { assessorOutput: Record<string, unknown> }) {
  const [showRaw, setShowRaw] = useState(false);
  const appliedRules = assessorOutput.applied_rules as
    | { rule: string; matched: boolean; evidence: string }[]
    | undefined;

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-xs font-semibold tracking-wider text-gray-500 uppercase">
          Assessor Output
        </h4>
        <button
          onClick={() => setShowRaw(!showRaw)}
          className="text-xs text-gray-500 underline hover:text-gray-700"
        >
          {showRaw ? 'Hide raw JSON' : 'Show raw JSON'}
        </button>
      </div>

      {appliedRules && !showRaw && (
        <div className="space-y-1">
          <div className="mb-2 flex gap-3 text-xs text-gray-600">
            <span>Total: {appliedRules.length}</span>
            <span>Matched: {appliedRules.filter(r => r.matched).length}</span>
            <span>Not matched: {appliedRules.filter(r => !r.matched).length}</span>
          </div>
          <div className="max-h-72 space-y-1 overflow-y-auto">
            {appliedRules.map((rule, i) => (
              <div
                key={i}
                className="rounded border border-gray-200 bg-gray-50 p-2 text-xs"
              >
                <div className="flex items-start gap-2">
                  <span
                    className={`mt-0.5 inline-block h-2 w-2 flex-shrink-0 rounded-full ${rule.matched ? 'bg-gray-900' : 'bg-gray-300'}`}
                  />
                  <div>
                    <span className={`text-gray-900 ${rule.matched ? 'font-medium' : ''}`}>
                      {rule.rule}
                    </span>
                    <div className="mt-0.5 text-gray-600">{rule.evidence}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showRaw && (
        <pre className="max-h-96 overflow-auto rounded bg-gray-900 p-3 text-xs text-gray-100">
          {JSON.stringify(assessorOutput, null, 2)}
        </pre>
      )}
    </div>
  );
}

function FlaggedItemCard({
  item,
}: {
  item: StructuredUnderwritingResult['flagged_items'][number];
}) {
  const isLowConfidence = item.confidence === 'low';

  return (
    <div
      className={`rounded-lg border p-3 ${isLowConfidence ? 'border-gray-100 bg-gray-50/50' : 'border-gray-200 bg-white'}`}
    >
      <div className="space-y-1.5">
        <div className="flex items-start gap-2">
          <span className={`flex-1 text-sm ${isLowConfidence ? 'text-gray-500' : 'text-gray-900'}`}>
            {item.applicant && <span className="font-medium">{item.applicant}: </span>}
            {item.description}
          </span>
          {item.confidence && item.confidence !== 'high' && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className={`mt-0.5 flex-shrink-0 cursor-help rounded px-1.5 py-0.5 text-[10px] font-medium tracking-wide uppercase ${
                    item.confidence === 'low'
                      ? 'bg-gray-100 text-gray-400'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {item.confidence}
                </button>
              </TooltipTrigger>
              <TooltipContent>Model Confidence Level</TooltipContent>
            </Tooltip>
          )}
        </div>
        {item.confidence_reasoning && (
          <p className={`text-xs ${isLowConfidence ? 'text-gray-500' : 'text-gray-600'}`}>
            {item.confidence_reasoning}
          </p>
        )}
        {item.source && (
          <p
            className={`text-xs break-words italic ${isLowConfidence ? 'text-gray-500' : 'text-gray-600'}`}
          >
            Source: {item.source}
          </p>
        )}
      </div>
    </div>
  );
}

export function StructuredSlideOutContent({
  structuredResult,
  pipelineMetadata,
}: StructuredSlideOutContentProps) {
  const session = useSelector(selectSession);
  const isWizard = session.sessionType === SessionType.wizard;
  const showDiagnostics = isWizard && pipelineMetadata;

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    overview: true,
    applicants: true,
    flagged: true,
    recommendations: true,
  });

  const toggleSection = (key: string) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  const hasApplicants = structuredResult.applicants && structuredResult.applicants.length > 0;

  return (
    <div className="space-y-3">
      <Disclaimer />

      {/* Outcome Summary */}
      <div className="px-1 py-2">
        <p className="text-sm font-medium text-gray-900">
          Projected Eligibility:{' '}
          <span className={getOutcomeConfig(structuredResult.outcome).classes.textColor}>
            {getOutcomeLabel(structuredResult.outcome)}
          </span>
        </p>
        {structuredResult.rate_class !== 'not_available' && (
          <p className="mt-1 text-xs text-gray-500">
            Rate Class: {RATE_CLASS_LABELS[structuredResult.rate_class]}
          </p>
        )}
      </div>

      {/* Eligibility Overview */}
      <Collapsible
        open={openSections.overview}
        onOpenChange={() => toggleSection('overview')}
      >
        <CollapsibleTrigger className="w-full rounded-lg px-1 py-2 hover:bg-gray-50">
          <SectionHeader
            title="Eligibility Overview"
            isOpen={openSections.overview}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-1 pt-1 pb-2">
            <p className="text-sm leading-relaxed text-gray-700">
              {structuredResult.eligibility_overview}
            </p>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Per-Applicant Verdicts (joint only) */}
      {hasApplicants && (
        <Collapsible
          open={openSections.applicants}
          onOpenChange={() => toggleSection('applicants')}
        >
          <CollapsibleTrigger className="w-full rounded-lg px-1 py-2 hover:bg-gray-50">
            <SectionHeader
              title="Individual Assessments"
              count={structuredResult.applicants!.length}
              isOpen={openSections.applicants}
            />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="space-y-2 px-1 pt-1 pb-2">
              {structuredResult.applicants!.map((applicant, index) => (
                <div
                  key={index}
                  className="rounded-lg border border-gray-200 p-3"
                >
                  <div className="mb-2 flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{applicant.name}</span>
                    <StatusBadge outcome={applicant.outcome} />
                    {applicant.rate_class !== 'not_available' && (
                      <span className="text-xs text-gray-500">
                        {RATE_CLASS_LABELS[applicant.rate_class]}
                      </span>
                    )}
                  </div>
                  <p className="text-sm leading-relaxed text-gray-700">{applicant.rationale}</p>
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Flagged Items */}
      <Collapsible
        open={openSections.flagged}
        onOpenChange={() => toggleSection('flagged')}
      >
        <CollapsibleTrigger className="w-full rounded-lg px-1 py-2 hover:bg-gray-50">
          <SectionHeader
            title="Flagged Items"
            count={structuredResult.flagged_items.length}
            isOpen={openSections.flagged}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-1 pt-1 pb-2">
            {structuredResult.flagged_items.length > 0 ? (
              <div className="space-y-2">
                {[...structuredResult.flagged_items]
                  .sort((a, b) => {
                    const order: Record<string, number> = { high: 0, medium: 1, low: 2 };
                    return (order[a.confidence] ?? 1) - (order[b.confidence] ?? 1);
                  })
                  .map((item, index) => (
                    <FlaggedItemCard
                      key={index}
                      item={item}
                    />
                  ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No items flagged.</p>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Recommendations */}
      <Collapsible
        open={openSections.recommendations}
        onOpenChange={() => toggleSection('recommendations')}
      >
        <CollapsibleTrigger className="w-full rounded-lg px-1 py-2 hover:bg-gray-50">
          <SectionHeader
            title="Review Points"
            count={structuredResult.recommendations.length}
            isOpen={openSections.recommendations}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-1 pt-1 pb-2">
            {structuredResult.recommendations.length > 0 ? (
              <div className="space-y-2">
                {structuredResult.recommendations.map((item, index) => (
                  <div
                    key={index}
                    className="rounded-lg border border-gray-200 p-3"
                  >
                    <p className="text-sm text-gray-900">
                      {item.applicant && <span className="font-medium">{item.applicant}: </span>}
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No review points.</p>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Wizard Diagnostics Button */}
      {showDiagnostics && (
        <div className="pt-2">
          <WizardDiagnosticsDialog
            structuredResult={structuredResult}
            pipelineMetadata={pipelineMetadata}
          />
        </div>
      )}
    </div>
  );
}
